import React, { FC, useCallback, useMemo } from 'react';

import { useSelector } from 'react-redux';

import { LoadingButton } from '@atlaskit/button';
import RetryIcon from '@atlaskit/icon/glyph/retry';
import { Flex } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import { getIsCurrentPullRequestOpen } from 'src/redux/pull-request/selectors';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { MergeCheck } from 'src/types';
import { ForgeMergeCheckResult } from 'src/types/custom-merge-checks';

import {
  ClassicMergeCheckItem,
  CustomMergeCheckItem,
} from '../../merge-check-item';
import {
  isClassicMergeCheck,
  isForgeMergeCheckResult,
  makeClassicCheckKey,
  makeCustomCheckKey,
} from '../../utils';
import { mergeCheckResultComparator } from '../utils';

import { useMergeResultsPopupProps, useRetryMergeChecks } from './hooks';
import { messages } from './rich-merge-checks-list.i18n';
import { Divider, OnMergeDescription } from './styled';

export type RichMergeChecksListProps = {
  preMergeCheckResults: ForgeMergeCheckResult[];
  onMergeCheckResults: ForgeMergeCheckResult[];
  classicMergeCheckResults: MergeCheck[];
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const RichMergeChecksList: FC<RichMergeChecksListProps> = ({
  preMergeCheckResults,
  onMergeCheckResults,
  classicMergeCheckResults,
}) => {
  const { formatMessage } = useIntl();
  const { retriedResultUuids, retryCheck } = useRetryMergeChecks();
  const { keyPopupIsOpenFor, showPopup, hidePopup, mouseCallbacks } =
    useMergeResultsPopupProps(100);

  const allPreMergeCheckResults = useMemo(() => {
    const results = [...preMergeCheckResults, ...classicMergeCheckResults];
    results.sort(mergeCheckResultComparator);

    return results;
  }, [preMergeCheckResults, classicMergeCheckResults]);

  const isPrOpen = useSelector(getIsCurrentPullRequestOpen);
  const repoAccessLevel = useSelector(getRepositoryAccessLevel);
  const hasRepoWriteAccess =
    repoAccessLevel === 'write' || repoAccessLevel === 'admin';

  const renderCustomPreMergeCheckResultTrailing = useCallback(
    (result: ForgeMergeCheckResult) => {
      if (
        isPrOpen &&
        (result.status === 'FAILED' || result.status === 'ERROR')
      ) {
        const rerunCheckI18n = formatMessage(messages.rerunCheck);
        const tooltipContent = hasRepoWriteAccess
          ? rerunCheckI18n
          : formatMessage(messages.noRepositoryWrite);

        return (
          <Tooltip position="bottom" content={tooltipContent}>
            <LoadingButton
              iconBefore={<RetryIcon size="medium" label={rerunCheckI18n} />}
              isLoading={result.id ? retriedResultUuids.has(result.id) : false}
              onClick={() => retryCheck(result.id)}
              spacing="compact"
              testId="rerunCheckButton"
              isDisabled={!hasRepoWriteAccess}
            />
          </Tooltip>
        );
      }

      return null;
    },
    [
      isPrOpen,
      hasRepoWriteAccess,
      formatMessage,
      retriedResultUuids,
      retryCheck,
    ]
  );

  const renderCustomPreMergeCheckResults = useCallback(
    (result: ForgeMergeCheckResult) => {
      const key = makeCustomCheckKey(result);
      return (
        <CustomMergeCheckItem
          key={key}
          mergeCheckKey={key}
          result={result}
          popupIsOpen={key === keyPopupIsOpenFor}
          showPopup={showPopup}
          hidePopup={hidePopup}
          mouseCallbacks={mouseCallbacks}
          trailing={renderCustomPreMergeCheckResultTrailing(result)}
        />
      );
    },
    [
      hidePopup,
      keyPopupIsOpenFor,
      mouseCallbacks,
      renderCustomPreMergeCheckResultTrailing,
      showPopup,
    ]
  );

  return (
    <div data-testid="mergeCheckList">
      <Flex direction="column" gap="space.100">
        {allPreMergeCheckResults.map(result => {
          if (isForgeMergeCheckResult(result)) {
            return renderCustomPreMergeCheckResults(result);
          } else if (isClassicMergeCheck(result)) {
            const key = makeClassicCheckKey(result);
            return (
              <ClassicMergeCheckItem
                key={key}
                mergeCheckKey={key}
                check={result}
                popupIsOpen={key === keyPopupIsOpenFor}
                showPopup={showPopup}
                hidePopup={hidePopup}
                mouseCallbacks={mouseCallbacks}
              />
            );
          }
          return null;
        })}
      </Flex>

      {allPreMergeCheckResults.length > 0 && onMergeCheckResults.length > 0 && (
        <Divider />
      )}

      {onMergeCheckResults.length > 0 && (
        <Flex direction="column" gap="space.100" testId="onMergeCheckResults">
          <OnMergeDescription>
            {isPrOpen
              ? formatMessage(messages.onMergeOpenPrDescription)
              : formatMessage(messages.onMergeClosedPrDescription)}
          </OnMergeDescription>
          {onMergeCheckResults.map(result => {
            const key = makeCustomCheckKey(result);
            return (
              <CustomMergeCheckItem
                key={key}
                mergeCheckKey={key}
                result={result}
                popupIsOpen={key === keyPopupIsOpenFor}
                showPopup={showPopup}
                hidePopup={hidePopup}
                mouseCallbacks={mouseCallbacks}
              />
            );
          })}
        </Flex>
      )}
    </div>
  );
};
